"use client";

import { useEffect } from "react";
import { queueConsentModeUpdate } from "@/lib/analytics/consent";
import { useConsent } from "@/hooks/useConsent";
import {
  CROSS_DOMAIN_LINKER_DOMAINS,
} from "@/lib/analytics/trackingConsent";
import { scrubAdvertisingBrowserUrl } from "@/lib/privacy/urlSanitization";

type GTMConsentLoaderProps = {
  gtmId?: string | null;
  nonce?: string;
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    __saGtmLoaded?: boolean;
    __saGtmConsentSignature?: string;
    __saAnalyticsConsentEventSent?: boolean;
    __saMarketingConsentEventSent?: boolean;
  }
}

function canLoadGtm(gtmId: string | null | undefined, hasTrackingConsent: boolean) {
  return process.env.NODE_ENV === "production" && Boolean(gtmId?.trim()) && hasTrackingConsent;
}

function configureAnalyticsLinker(enabled: boolean) {
  window.gtag?.("set", "linker", {
    domains: enabled ? CROSS_DOMAIN_LINKER_DOMAINS : [],
    accept_incoming: enabled,
    decorate_forms: false,
    url_passthrough: false,
  });
}

function syncAnalyticsConsentEvent(analytics: boolean) {
  if (!analytics) {
    window.__saAnalyticsConsentEventSent = false;
    return;
  }
  if (window.__saAnalyticsConsentEventSent || !window.dataLayer) return;
  window.dataLayer.push({ event: "sa_analytics_consent_granted" });
  window.__saAnalyticsConsentEventSent = true;
}

function syncMarketingConsentEvent(marketing: boolean) {
  if (!marketing) {
    window.__saMarketingConsentEventSent = false;
    return;
  }
  if (window.__saMarketingConsentEventSent || !window.dataLayer) return;
  window.dataLayer.push({ event: "sa_marketing_consent_granted" });
  window.__saMarketingConsentEventSent = true;
}

function initializeConsent(consent: { analytics: boolean; marketing: boolean }) {
  queueConsentModeUpdate({
    analytics: consent.analytics ? "granted" : "denied",
    marketing: consent.marketing ? "granted" : "denied",
  });
  window.__saGtmConsentSignature = `${consent.analytics ? "1" : "0"}:${consent.marketing ? "1" : "0"}`;
  configureAnalyticsLinker(consent.analytics);
}

function updateLoadedConsent(consent: { analytics: boolean; marketing: boolean }) {
  const signature = `${consent.analytics ? "1" : "0"}:${consent.marketing ? "1" : "0"}`;
  if (!window.__saGtmLoaded || typeof window.gtag !== "function" || window.__saGtmConsentSignature === signature) {
    return;
  }
  queueConsentModeUpdate({
    analytics: consent.analytics ? "granted" : "denied",
    marketing: consent.marketing ? "granted" : "denied",
  });
  window.__saGtmConsentSignature = signature;
  configureAnalyticsLinker(consent.analytics);
  syncAnalyticsConsentEvent(consent.analytics);
  syncMarketingConsentEvent(consent.marketing);
}

export function GTMConsentLoader({ gtmId, nonce }: GTMConsentLoaderProps) {
  const { consent } = useConsent();
  const normalizedGtmId = gtmId?.trim();
  const shouldLoad = canLoadGtm(normalizedGtmId, consent.analytics || consent.marketing);

  useEffect(() => {
    if (!shouldLoad || !normalizedGtmId || window.__saGtmLoaded) return;

    if (!consent.marketing) scrubAdvertisingBrowserUrl();
    initializeConsent(consent);
    window.dataLayer?.push({ "gtm.start": Date.now(), event: "gtm.js" });
    syncAnalyticsConsentEvent(consent.analytics);
    syncMarketingConsentEvent(consent.marketing);

    const script = document.createElement("script");
    script.id = "sa-gtm";
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(normalizedGtmId)}`;
    if (nonce) script.nonce = nonce;
    document.head.appendChild(script);
    window.__saGtmLoaded = true;
  }, [consent, nonce, normalizedGtmId, shouldLoad]);

  useEffect(() => {
    updateLoadedConsent(consent);
  }, [consent]);

  return null;
}
